import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import BASE_URL from '../config/config';
import * as XLSX from 'xlsx';
import Loader from '../components/Loader';

const Clientes = () => {
  // Estados principales
  const [proyectos, setProyectos] = useState([]);
  const [filter, setFilter] = useState('2025');
  const [maintenanceFilter, setMaintenanceFilter] = useState('all');
  const [childFilter, setChildFilter] = useState('all');
  const [nameFilter, setNameFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('default'); // Estado para la ordenación
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const itemsPerPage = 20;
  const DEFAULT_COST_HOUR = 30;

  // Estadísticas de tags padre
  const tagStats = {
    '5retainer': 'Igualas Totales',
    '3retdesarrollo': 'Igualas Desarrollo',
    '3retcreativo': 'Igualas Creativo',
    '3rrss': 'Igualas RRSS',
    '3seo': 'Igualas SEO',
    '5oneshot': 'Oneshot'
  };

  // Definición de tags hijos para ciertos tags padre
  const childTags = {
    '3retdesarrollo': {
      '4mtotecnico': 'Mantenimiento técnico 0 horas',
      '4mto3horas': 'Mantenimiento técnico 3 horas',
      '4mto5horas': 'Mantenimiento técnico 5 horas',
      '4mto7horas': 'Mantenimiento técnico 7 horas',
      '4mtoamedida': 'Mantenimiento técnico a medida'
    },
    '3retcreativo': {
      '4crrss4horas': 'Contenido redes sociales 4 horas',
      '4crrss8horas': 'Contenido redes sociales 8 horas',
      '4crrss12horas': 'Contenido redes sociales 12 horas'
    },
    '3rrss': {
      '4copy3post': 'RRSS - 3 posts',
      '4copy5post': 'RRSS - 5 posts',
      '4copy10post': 'RRSS - 10 posts',
      '4linkedin': 'Redes Adicionales (LinkedIn)',
      '4tiktok': 'Redes Adicionales (TikTok)',
      '4twitter': 'Redes Adicionales (Twitter)'
    }
  };

  // Efecto para cargar los datos desde el archivo txt
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/cachedFiles/todosLosProyectos.txt`);
        setProyectos(response.data);
      } catch (error) {
        console.error('Error al cargar los datos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Loader/>
    );
  }

  // Función para calcular el coste total en euros de un proyecto
  const calcularCosteTotal = (proyecto) => {
    return proyecto.timeTracking.reduce((total, entry) => {
      const durationHours = entry.duration / 3600;
      const costHour = entry.costHour !== 0 ? entry.costHour : DEFAULT_COST_HOUR;
      return total + (durationHours * costHour);
    }, 0).toFixed(2);
  };

  // Filtrado por año
  const filterByYear = (project, year) => {
    if (year === 'all') return true;
    const startOfYear = new Date(year, 0, 1).getTime() / 1000;
    const endOfYear = new Date(year, 11, 31, 23, 59, 59).getTime() / 1000;
    return project.date >= startOfYear && project.dueDate <= endOfYear;
  };

  // Filtrado por año y nombre
  const filteredProjects = proyectos.filter(proyecto =>
    filterByYear(proyecto, filter) &&
    (nameFilter === '' || proyecto.name.toLowerCase().includes(nameFilter.toLowerCase()))
  );

  // --- Función para obtener el presupuesto de Antonio ---
  const getAntonioBudget = (project, type) => {
    const antonioId = "5e30324f6a972831026ffdc2";
    const budget = project.users[antonioId]?.totalbudgetamount || 0;
    return type === 'monthly' ? (budget / 12).toFixed(2) : budget;
  };

  // Filtrado por tags (padre y opcionalmente hijo)
  const filteredProjectsByTags = filteredProjects.filter(proyecto =>
    (maintenanceFilter === 'all' || (proyecto.tags && proyecto.tags.includes(maintenanceFilter))) &&
    (childFilter === 'all' || (proyecto.tags && proyecto.tags.includes(childFilter)))
  );

  // Ordenación de proyectos por euros consumidos
  // "mas" = de menor a mayor coste (más 'rentable' si consumen menos)
  // "menos" = de mayor a menor coste (menos 'rentable' si consumen más)
  let sortedProjects = [...filteredProjectsByTags];
  if (sortOrder === 'mas') {
    sortedProjects.sort((a, b) => parseFloat(calcularCosteTotal(a)) - parseFloat(calcularCosteTotal(b)));
  } else if (sortOrder === 'menos') {
    sortedProjects.sort((a, b) => parseFloat(calcularCosteTotal(b)) - parseFloat(calcularCosteTotal(a)));
  }
  // Si sortOrder === 'default', no hacemos nada, se quedan en el orden que vienen

  // Paginación
  const paginatedProjects = sortedProjects.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Contador de tags
  const tagCounts = Object.keys(tagStats).reduce((acc, tag) => {
    acc[tag] = filteredProjects.filter(proyecto => proyecto.tags?.includes(tag)).length;
    return acc;
  }, {});

  // Handlers para cambiar los filtros
  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setCurrentPage(1);
  };

  // Al cambiar el filtro padre, se resetea también el hijo
  const handleMaintenanceFilterChange = (e) => {
    setMaintenanceFilter(e.target.value);
    setChildFilter('all');
    setCurrentPage(1);
  };

  // Suma la duración total en horas y minutos de un proyecto
  const sumTotalDuration = (project) => {
    if (!project.timeTracking?.length) return '0h 0min';
    const totalSeconds = project.timeTracking.reduce((acc, entry) => acc + (entry.duration || 0), 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}h ${minutes}min`;
  };

  // Componente para la barra de progreso
  const ProgressBar = ({ eurosConsumidos, facturacionAnual }) => {
    const porcentaje = (eurosConsumidos / facturacionAnual) * 100;
    const sobrepasado = eurosConsumidos > facturacionAnual;
    const exceso = eurosConsumidos - facturacionAnual;

    return (
      <div
        className="barra-de-progreso"
        style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '8px', overflow: 'hidden' }}
      >
        <div
          style={{
            width: `${Math.min(porcentaje, 100)}%`,
            backgroundColor: sobrepasado ? '#e74c3c' : '#2ecc71',
            textAlign: 'center',
            fontWeight: 'bold',
          }}
        >
          <p>{sobrepasado ? `-${exceso.toFixed(2)}€` : `${porcentaje.toFixed(2)}%`}</p>
        </div>
      </div>
    );
  };

  // Exportar a Excel
  const exportToExcel = () => {
    const exportData = paginatedProjects.map(proyecto => {
      const facturacionAnual = proyecto.users['5e30324f6a972831026ffdc2']?.totalbudgetamount || 0;
      const eurosConsumidos = proyecto.timeTracking?.reduce((total, entry) =>
        total + (entry.duration / 3600 * (entry.costHour || DEFAULT_COST_HOUR)), 0
      ).toFixed(2);
      const progreso = facturacionAnual > 0
        ? ((eurosConsumidos / facturacionAnual) * 100).toFixed(2) + '%'
        : '-' + eurosConsumidos + '€';

      return {
        Proyecto: proyecto.name,
        Inicio: new Date(proyecto.date * 1000).toLocaleDateString('es-ES'),
        Fin: new Date(proyecto.dueDate * 1000).toLocaleDateString('es-ES'),
        Tags: proyecto.tags && proyecto.tags.length > 0 ? proyecto.tags.join(', ') : 'Sin etiquetas',
        Estado: proyecto.archived
          ? `Archivado (${new Date(proyecto.archived * 1000).toLocaleDateString('es-ES')})`
          : 'Activo',
        'Facturación Anual (€)': facturacionAnual,
        'Facturación Mensual (€)': (facturacionAnual / 12).toFixed(2),
        'Horas Consumidas': proyecto.timeTracking?.reduce((acc, entry) => acc + (entry.duration || 0), 0) / 3600,
        'Euros Consumidos (€)': eurosConsumidos,
        Progreso: progreso
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Proyectos');
    XLSX.writeFile(wb, 'proyectos_exportados.xlsx');
  };

  return (
    <main className="bg-gris">
      <div className="site-width dashboard-grid">
        <div className="dashboard-content">
          <h2>Servicios & Clientes</h2>
          <p>Consulta los servicios contratados por los clientes y su rentabilidad.</p>

          {/* Estadísticas de tags */}
          <div className="stats-container">
            <div className="circle-principal">
              <p>Total</p>
              <div className="stat-circle stat-total" style={{ '--progress': 100, '--color': '#2ecc71' }}>
                <p>{filteredProjects.length}</p>
              </div>
            </div>

            {Object.entries(tagStats).map(([tag, label]) => (
              <div
                className="circle-principal"
                key={tag}
                onClick={() => {
                  setMaintenanceFilter(tag);
                  setChildFilter('all');
                  setCurrentPage(1);
                }}
                style={{ cursor: 'pointer' }}
              >
                <p>{label}</p>
                <div
                  className="stat-circle"
                  style={{ '--progress': (tagCounts[tag] / filteredProjects.length) * 100, '--color': '#3498db' }}
                >
                  <p>
                    <b>{tagCounts[tag]}</b>
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Filtros y botón de exportación */}
          <div className="grid-selector-servicios">
            <input
              type="text"
              placeholder="Filtrar por nombre"
              value={nameFilter}
              onChange={handleFilterChange(setNameFilter)}
            />

            <select onChange={handleFilterChange(setFilter)} value={filter}>
              {[2025, 2024, 2023, 2022, 2021, 2020].map((year) => (
                <option key={year} value={year}>
                  Proyectos {year}
                </option>
              ))}
              <option value="all">Mostrar Todos</option>
            </select>

            <select onChange={handleMaintenanceFilterChange} value={maintenanceFilter}>
              <option value="all">Todos los tags</option>
              {Object.entries(tagStats).map(([tag, label]) => (
                <option key={tag} value={tag}>
                  {label}
                </option>
              ))}
            </select>

            {childTags[maintenanceFilter] && (
              <select value={childFilter} onChange={handleFilterChange(setChildFilter)}>
                <option value="all">Todos</option>
                {Object.entries(childTags[maintenanceFilter]).map(([childKey, childLabel]) => (
                  <option key={childKey} value={childKey}>
                    {childLabel}
                  </option>
                ))}
              </select>
            )}

            {/* Selector para ordenar por rentabilidad (en base a euros consumidos) */}
            <select onChange={handleFilterChange(setSortOrder)} value={sortOrder}>
              <option value="default">Ordenar por rentabilidad</option>
              <option value="mas">Más rentable (menos consumido)</option>
              <option value="menos">Menos rentable (más consumido)</option>
            </select>

            <button className="excel-export" onClick={exportToExcel}>
              <img src="/images/sobresalir.svg" alt="Excel" />
              <p>Exportar a Excel</p>
            </button>
          </div>

          {/* Tabla de proyectos */}
          <div className="proyectos-table">
            <div className="table-header">
              <div className="table-row">
                <div className="table-cell">
                  <p>Nombre</p>
                </div>
                <div className="table-cell">
                  <p>Estado</p>
                </div>
                <div className="table-cell">
                  <p>Facturación Anual (€)</p>
                </div>
                <div className="table-cell">
                  <p>Facturación Mensual (€)</p>
                </div>
                <div className="table-cell">
                  <p>Horas Consumidas</p>
                </div>
                <div className="table-cell">
                  <p>Euros Consumidos (€)</p>
                </div>
                <div className="table-cell">
                  <p>Progreso</p>
                </div>
              </div>
            </div>

            <div className="table-body">
              {paginatedProjects.map((proyecto, index) => (
                <Link
                  to={`/proyecto/${proyecto.id}`}
                  className="table-row"
                  key={index}
                  onClick={() => window.scrollTo(0, 0)}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="table-cell cell-datos-proyecto">
                    <p>{proyecto.name}</p>
                    <div className="cell-fecha">
                      <p className="cell-fecha-inicio">
                        Inicio: {new Date(proyecto.date * 1000).toLocaleDateString('es-ES')}
                      </p>
                      <p className="cell-fecha-fin">
                        Fin: {new Date(proyecto.dueDate * 1000).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <div className="cell-tags">
                      {proyecto.tags && proyecto.tags.length > 0 ? (
                        proyecto.tags.map((tag, i) => (
                          <span key={i} className="tag">
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="tag tag-empty">Sin etiquetas</span>
                      )}
                    </div>
                  </div>
                  <div className="table-cell">
                    {proyecto.archived ? (
                      <p className="archivado">
                        Este proyecto se archivó el{' '}
                        <b>{new Date(proyecto.archived * 1000).toLocaleDateString('es-ES')}</b>
                      </p>
                    ) : (
                      <p className="activo">ACTIVO</p>
                    )}
                  </div>
                  <div className="table-cell">
                    <p>{getAntonioBudget(proyecto, 'total')}€</p>
                  </div>
                  <div className="table-cell">
                    <p>{getAntonioBudget(proyecto, 'monthly')}€</p>
                  </div>
                  <div className="table-cell">
                    <p>{sumTotalDuration(proyecto)}</p>
                  </div>
                  <div className="table-cell">
                    <p>{calcularCosteTotal(proyecto)}€</p>
                  </div>
                  <div className="table-cell">
                    <ProgressBar
                      eurosConsumidos={parseFloat(calcularCosteTotal(proyecto))}
                      facturacionAnual={parseFloat(getAntonioBudget(proyecto, 'total'))}
                    />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Paginación */}
          <div className="proyectos-paginacion">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
              Anterior
            </button>
            <span>Página {currentPage}</span>
            <button
              disabled={paginatedProjects.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Clientes;
